/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useForm, SubmitHandler } from "react-hook-form";
import {
  Button,
  Input,
  Spinner,
  FlexContainer,
  Text,
  Description,
  theme,
} from "@nordcloud/gnui";
import { EditButton } from "~/components/Buttons";
import { useToggle } from "~/hooks";
import { truncateString } from "~/tools";

type FormData = { value: string };

type Props = {
  value: string | null | undefined;
  isLoading: boolean;
  required?: boolean;
  onSubmit: SubmitHandler<FormData>;
};

export function InlineUpdate({
  value,
  isLoading,
  required = true,
  onSubmit,
}: Props) {
  const [isEditMode, toggleEditMode] = useToggle(false);

  const {
    register,
    handleSubmit,

    formState: { errors },
  } = useForm();

  if (isLoading) {
    return (
      <>
        <Spinner size="sm" />
        <Text weight="regular" tag="div" ml={theme.spacing.spacing04}>
          Loading...
        </Text>
      </>
    );
  }

  const inputId = "value";

  if (isEditMode) {
    return (
      <form
        css={{ display: "block" }}
        onSubmit={handleSubmit<FormData>((data) => {
          toggleEditMode();
          onSubmit(data);
        })}
      >
        <FlexContainer>
          <Input
            type="text"
            {...register(inputId, { required })}
            small
            status={errors.name && "error"}
            defaultValue={value ?? ""}
          />
          <Button
            ml={theme.spacing.spacing02}
            type="submit"
            icon="save"
            size="md"
            severity="low"
          >
            Save
          </Button>
          <Button
            ml={theme.spacing.spacing01}
            icon="close"
            size="md"
            severity="low"
            onClick={toggleEditMode}
          >
            Cancel
          </Button>
        </FlexContainer>
        {required && errors[inputId] != null && (
          <Description>This field is required.</Description>
        )}
      </form>
    );
  }

  return (
    <FlexContainer alignItems="center">
      <Text as="div" m="0" mr={theme.spacing.spacing02}>
        {truncateString(value ?? "", 80)}
      </Text>
      <EditButton onClick={toggleEditMode} />
    </FlexContainer>
  );
}
